import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { isSeriesFinished } from '../../utils/continueWatching';
import {
  Tv2,
  Play,
  Star,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  Layers,
  Sparkles,
  Zap,
  LayoutGrid,
  List as ListIcon,
  SlidersHorizontal,
  Check,
  RotateCcw,
  Clock,
  Loader2,
  History,
  CheckCircle2,
  Menu,
  Trash2,
} from 'lucide-react';
import { useIPTV } from '../../context/IPTVContext';
import { Series } from '../../types/iptv';
import { RatingsBadges } from '../common/RatingsBadges';
import { SeriesPoster } from '../common/SeriesPoster';
import { AutoScrollTitle } from '../common/AutoScrollTitle';
import { OpenTvLoader } from '../common/OpenTvLoader';
import { getSafeImageUrl } from '../../utils/imageUtils';
import { extractReleaseYear } from '../../utils/m3uParser';
import { shouldHideCategoryFromList, shouldHideContentFromList } from '../../utils/security';
import { fetchTmdbSeriesInfo, TmdbMovieMetadata } from '../../utils/tmdb';
import { EmptyCategoryGuide } from '../common/EmptyCategoryGuide';

const getDisplayYear = (title: string, year?: number) => {
  if (year && year >= 1920 && year <= 2025) return year;
  const extracted = extractReleaseYear(title);
  if (extracted >= 1920 && extracted <= 2026) return extracted;
  return year && year >= 1920 && year <= 2026 ? year : undefined;
};

export const SeriesView: React.FC = () => {
  const {
    series,
    seriesCategories,
    rawSeriesCategories,
    categoryConfig,
    activeSeriesCategory,
    setActiveSeriesCategory,
    activeSeriesDetail,
    setActiveSeriesDetail,
    playEpisode,
    isFavorite,
    toggleFavorite,
    isCategoryFavorite,
    toggleCategoryFavorite,
    currentProfile,
    parentalConfig,
    getContentProgress,
    getSeriesProgress,
    clearContentProgress,
    clearAllWatchHistory,
    isContentWatched,
    toggleWatched,
    loadSeriesEpisodes,
  } = useIPTV();

  // Estado independente para filtrar categorias na barra lateral/gaveta
  const [categoryFilter, setCategoryFilter] = useState('');
  // Estado independente para buscar séries na grade
  const [seriesSearch, setSeriesSearch] = useState('');
  const [selectedDetailSeries, setSelectedDetailSeries] = useState<Series | null>(() => activeSeriesDetail);
  const [tmdbDetails, setTmdbDetails] = useState<TmdbMovieMetadata | null>(null);
  const [isFetchingTmdb, setIsFetchingTmdb] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState<number>(1);
  const [isLoadingSeriesDetails, setIsLoadingSeriesDetails] = useState(false);
  const [seriesDetailsError, setSeriesDetailsError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');
  const [visibleLimit, setVisibleLimit] = useState(60);
  const [isMobileCategoryDrawerOpen, setIsMobileCategoryDrawerOpen] = useState(false);
  const contentAreaRef = useRef<HTMLDivElement>(null);
  const seasonsScrollRef = useRef<HTMLDivElement>(null);

  // Enriquecimento automático de sinopse e capa via TMDB se a lista IPTV vier sem sinopse completa
  useEffect(() => {
    if (!selectedDetailSeries) {
      setTmdbDetails(null);
      setIsFetchingTmdb(false);
      return;
    }

    const currentSynopsis = selectedDetailSeries.synopsis || selectedDetailSeries.description;
    const hasShortOrNoDesc = !currentSynopsis || currentSynopsis.trim().length < 25;
    if (hasShortOrNoDesc) {
      setIsFetchingTmdb(true);
      const parsedYear = getDisplayYear(selectedDetailSeries.title, selectedDetailSeries.year);
      fetchTmdbSeriesInfo(selectedDetailSeries.title, parsedYear)
        .then((meta) => {
          if (meta) {
            setTmdbDetails(meta);
          }
        })
        .finally(() => {
          setIsFetchingTmdb(false);
        });
    } else {
      setTmdbDetails(null);
      setIsFetchingTmdb(false);
    }
  }, [selectedDetailSeries]);

  const scrollSeasonsLeft = () => {
    if (seasonsScrollRef.current) {
      seasonsScrollRef.current.scrollBy({ left: -260, behavior: 'smooth' });
    }
  };

  const scrollSeasonsRight = () => {
    if (seasonsScrollRef.current) {
      seasonsScrollRef.current.scrollBy({ left: 260, behavior: 'smooth' });
    }
  };

  const continueWatchingSeriesCount = useMemo(() => {
    if (!currentProfile.watchHistory || currentProfile.watchHistory.length === 0) return 0;
    return series.filter((s) => {
      if (shouldHideCategoryFromList(s.category, currentProfile, parentalConfig)) return false;
      if (shouldHideContentFromList(s, currentProfile, parentalConfig)) return false;
      // Série terminada (assistida até o último episódio, sem novidade do provedor) sai do "Continuar Assistindo"
      if (isSeriesFinished(s, currentProfile.watchHistory, isContentWatched)) return false;
      return currentProfile.watchHistory?.some((h) => {
        if (!h || !h.contentId) return false;
        if (h.seriesId && (h.seriesId === s.id || h.seriesId === `ser-${s.id}` || h.seriesId === `ser-xtream-${s.id}`)) return true;
        if (h.contentId === s.id || h.contentId === `ser-${s.id}` || h.contentId === `ser-xtream-${s.id}`) return true;
        if (s.episodes && s.episodes.some((ep) => ep.id === h.contentId)) return true;
        return false;
      });
    }).length;
  }, [series, currentProfile, parentalConfig]);

  const handleSelectSeries = useCallback(async (ser: Series) => {
    setSelectedDetailSeries(ser);
    setSeriesSearch('');
    setCategoryFilter('');

    const calcFirstUnwatchedSeason = (episodesList: any[]) => {
      const realList = episodesList.filter(
        (e) => !e.id?.includes('-dummy') && e.title !== 'Carregar Episódios'
      );
      const listToUse = realList.length > 0 ? realList : episodesList;
      const available = Array.from(
        new Set(listToUse.map((ep) => Number(ep.seasonNumber || 1)))
      ).sort((a: number, b: number) => a - b);
      if (available.length === 0) return 1;

      // Encontra a primeira temporada com pelo menos um episódio não assistido
      const firstUnwatched = available.find((sNum) => {
        const seasonEps = listToUse.filter((ep) => Number(ep.seasonNumber || 1) === sNum);
        return seasonEps.some((ep) => !isContentWatched(ep.id));
      });

      return firstUnwatched || available[0];
    };

    const hasRealEpisodes =
      ser.episodes &&
      ser.episodes.length > 1 &&
      ser.episodes.some(
        (e: any) => !e.id?.includes('-dummy') && e.title !== 'Carregar Episódios'
      );

    setSeriesDetailsError(null);
    if (hasRealEpisodes) {
      setSelectedSeason(calcFirstUnwatchedSeason(ser.episodes));
      return;
    }

    setIsLoadingSeriesDetails(true);
    try {
      const full = await loadSeriesEpisodes(ser.id);
      if (full && Array.isArray(full.episodes) && full.episodes.length > 0) {
        const real = full.episodes.filter(
          (e) => !e.id?.includes('-dummy') && e.title !== 'Carregar Episódios'
        );
        if (real.length > 0) {
          setSelectedDetailSeries((prev) => {
            if (!prev || prev.id !== ser.id) return prev;
            return {
              ...prev,
              episodes: full.episodes,
              seasons: full.seasons || prev.seasons,
            };
          });
          setSelectedSeason(calcFirstUnwatchedSeason(full.episodes));
          setSeriesDetailsError(null);
        } else {
          setSeriesDetailsError('Nenhum capítulo disponível para esta série no momento.');
        }
      } else {
        setSeriesDetailsError('Não foi possível obter os episódios do servidor IPTV.');
      }
    } catch (err) {
      console.warn('Erro ao carregar episódios da série:', err);
      setSeriesDetailsError('Falha temporária ao comunicar com o servidor IPTV.');
    } finally {
      setIsLoadingSeriesDetails(false);
    }
  }, [loadSeriesEpisodes, isContentWatched]);

  // If user clicks a series in Continuar Assistindo from Home tab
  useEffect(() => {
    if (activeSeriesDetail) {
      handleSelectSeries(activeSeriesDetail);
      setActiveSeriesDetail(null);
    }
  }, [activeSeriesDetail, handleSelectSeries, setActiveSeriesDetail]);

  // Auto-scroll para a temporada selecionada na barra de temporadas
  useEffect(() => {
    if (selectedSeason && selectedDetailSeries) {
      const timer = setTimeout(() => {
        const el = document.getElementById(`season-btn-${selectedSeason}`);
        if (el && seasonsScrollRef.current) {
          el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedSeason, selectedDetailSeries?.id]);

  // Categorias para a barra lateral seguindo RIGOROSAMENTE a ordem definida no Gerenciador / Banco:
  // 1º Continuar Assistindo (se houver)
  // 2º Favoritos
  // 3º Categorias marcadas como favoritas com a Estrela ⭐ (no topo, logo abaixo de Favoritos)
  // 4º Demais Categorias na sequência EXATA configurada no Gerenciador / Banco
  // E ao desfavoritar, a categoria retorna automaticamente à sua posição original do banco!
  const filteredCategoryCards = useMemo(() => {
    // 1. Filtrar categorias ocultas por perfil / parental
    const visible = seriesCategories.filter(
      (cat) => !shouldHideCategoryFromList(cat, currentProfile, parentalConfig)
    );

    const specialPrefix: string[] = [];
    const regularCats: string[] = [];

    visible.forEach((cat) => {
      if (cat === 'Continuar Assistindo' || cat === 'Favoritos') {
        specialPrefix.push(cat);
      } else {
        regularCats.push(cat);
      }
    });

    // Separar categorias normais entre favoritadas com ⭐ e não favoritadas
    const starredCats: string[] = [];
    const unstarredCats: string[] = [];

    regularCats.forEach((cat) => {
      if (isCategoryFavorite('series', cat)) {
        starredCats.push(cat);
      } else {
        unstarredCats.push(cat);
      }
    });

    let fullList = [...specialPrefix, ...starredCats, ...unstarredCats];
    if (categoryFilter.trim()) {
      const q = categoryFilter.toLowerCase().trim();
      fullList = fullList.filter(
        (cat) => cat === 'Continuar Assistindo' || cat === 'Favoritos' || cat.toLowerCase().includes(q)
      );
    }
    return fullList;
  }, [seriesCategories, currentProfile, parentalConfig, isCategoryFavorite, categoryFilter]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const cat of filteredCategoryCards) {
      if (cat === 'Continuar Assistindo') {
        counts[cat] = continueWatchingSeriesCount;
      } else if (cat === 'Favoritos') {
        counts[cat] = series.filter((s) => isFavorite(s.id)).length;
      } else {
        const rename = categoryConfig.series.renames[cat];
        counts[cat] = series.filter((s) => s.category === cat || rename === cat).length;
      }
    }
    return counts;
  }, [filteredCategoryCards, series, isFavorite, continueWatchingSeriesCount, categoryConfig.series.renames]);

  // Filtered series inside active category or global search across all categories
  const categorySeries = useMemo(() => {
    const isSearching = !!seriesSearch.trim();
    const seriesHidden = new Set(categoryConfig?.series?.hidden || []);
    const seriesRenames = categoryConfig?.series?.renames || {};

    let list = series.filter((s) => {
      if (shouldHideContentFromList(s, currentProfile, parentalConfig)) return false;

      // Ocultar séries cujas categorias estejam ocultas no Gerenciador ou por Controle Parental
      const sCat = s.category || 'Geral';
      const rename = seriesRenames[sCat];
      if (seriesHidden.has(sCat) || (rename && seriesHidden.has(rename))) return false;
      if (shouldHideCategoryFromList(sCat, currentProfile, parentalConfig)) return false;
      if (rename && shouldHideCategoryFromList(rename, currentProfile, parentalConfig)) return false;

      if (isSearching) {
        const q = seriesSearch.toLowerCase().trim();
        const matchTitle = s.title.toLowerCase().includes(q);
        const matchCat = (s.category || '').toLowerCase().includes(q) || (rename && rename.toLowerCase().includes(q));
        const matchDesc = (s.synopsis || s.description || '').toLowerCase().includes(q);
        const matchYear = s.year ? s.year.toString().includes(q) : false;
        return matchTitle || matchCat || matchDesc || matchYear;
      }

      if (activeSeriesCategory === 'Favoritos') {
        if (!isFavorite(s.id)) return false;
      } else if (activeSeriesCategory === 'Continuar Assistindo') {
        const inHistory = currentProfile.watchHistory?.some((h) => {
          if (!h || !h.contentId) return false;
          if (h.seriesId && (h.seriesId === s.id || h.seriesId === `ser-${s.id}` || h.seriesId === `ser-xtream-${s.id}`)) return true;
          if (h.contentId === s.id || h.contentId === `ser-${s.id}` || h.contentId === `ser-xtream-${s.id}`) return true;
          if (s.episodes && s.episodes.some((ep) => ep.id === h.contentId)) return true;
          return false;
        });
        if (!inHistory) return false;
        // Terminada (e sem temporada/episódio novo) sai; se o provedor atualizar a série, volta
        if (isSeriesFinished(s, currentProfile.watchHistory, isContentWatched)) return false;
      } else if (activeSeriesCategory) {
        const rename = categoryConfig.series.renames[s.category];
        if (s.category !== activeSeriesCategory && rename !== activeSeriesCategory) {
          return false;
        }
      }

      return true;
    });

    if (activeSeriesCategory === 'Continuar Assistindo' && !isSearching) {
      list = [...list].sort((a, b) => {
        const histA = currentProfile.watchHistory?.filter((h) => h.contentId === a.id || a.episodes?.some(ep => ep.id === h.contentId));
        const histB = currentProfile.watchHistory?.filter((h) => h.contentId === b.id || b.episodes?.some(ep => ep.id === h.contentId));
        const latestA = histA && histA.length > 0 ? Math.max(...histA.map(h => new Date(h.updatedAt || 0).getTime())) : 0;
        const latestB = histB && histB.length > 0 ? Math.max(...histB.map(h => new Date(h.updatedAt || 0).getTime())) : 0;
        return latestB - latestA;
      });
    } else {
      list = [...list].sort((a, b) => {
        // 1. Year (descending)
        const yearA = getDisplayYear(a.title, a.year) || 0;
        const yearB = getDisplayYear(b.title, b.year) || 0;
        if (yearB !== yearA) return yearB - yearA;

        // 2. Score / Rating (descending)
        const scoreA = a.score || 0;
        const scoreB = b.score || 0;
        if (scoreB !== scoreA) return scoreB - scoreA;

        // 3. Assistido ou não (não assistidos primeiro)
        const isWatchedA = isContentWatched(a.id) ? 1 : 0;
        const isWatchedB = isContentWatched(b.id) ? 1 : 0;
        if (isWatchedA !== isWatchedB) return isWatchedA - isWatchedB;

        // 4. Title (A-Z)
        return a.title.localeCompare(b.title);
      });
    }

    return list;
  }, [
    series,
    activeSeriesCategory,
    categoryConfig.series,
    seriesSearch,
    isFavorite,
    isContentWatched,
    currentProfile.watchHistory,
    currentProfile,
    parentalConfig,
  ]);

  // Reset pagination limit, scroll to top when category or seriesSearch changes
  React.useEffect(() => {
    setVisibleLimit(60);
    if (contentAreaRef.current) {
      contentAreaRef.current.scrollTop = 0;
    }
  }, [activeSeriesCategory, seriesSearch]);

  // Autofocus first series ONLY when switching category via remote/keyboard (never while typing in an input)
  React.useEffect(() => {
    const isTyping = document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA';
    if (isTyping) return;

    const timer = setTimeout(() => {
      if (document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        const firstCard = contentAreaRef.current?.querySelector<HTMLElement>('[data-series-card="true"], [tabindex="0"]');
        firstCard?.focus();
      }
    }, 60);
    return () => clearTimeout(timer);
  }, [activeSeriesCategory]);

  const displayedSeries = useMemo(() => {
    return categorySeries.slice(0, visibleLimit);
  }, [categorySeries, visibleLimit]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 450) {
      if (displayedSeries.length < categorySeries.length) {
        setVisibleLimit((prev) => prev + 60);
      }
    }
  };

  // If a series is selected, show its season & episodes view
  if (selectedDetailSeries) {
    const seriesProg = getSeriesProgress(selectedDetailSeries);
    const hasSeriesProg = seriesProg?.lastEpisode && seriesProg?.episodeProgress && seriesProg.episodeProgress.watchedSeconds > 10;

    const episodesList = Array.isArray(selectedDetailSeries.episodes) ? selectedDetailSeries.episodes : [];

    const realEpisodes = episodesList.filter(
      (ep) => !ep.id?.includes('-dummy') && ep.title !== 'Carregar Episódios'
    );

    const hasLoadedRealEpisodes = realEpisodes.length > 0;

    const seasons = Array.from(
      new Set((hasLoadedRealEpisodes ? realEpisodes : episodesList).map((ep) => Number(ep.seasonNumber || 1)))
    ).sort((a: number, b: number) => a - b);
    if (seasons.length === 0) seasons.push(1);

    const currentSeason =
      seasons.length > 0 && !seasons.includes(Number(selectedSeason))
        ? seasons[0]
        : Number(selectedSeason);

    const episodesInSeason = (hasLoadedRealEpisodes ? realEpisodes : []).filter(
      (ep) => Number(ep.seasonNumber || 1) === currentSeason
    );

    return (
      <div className="w-full h-full flex flex-col bg-zinc-950 overflow-y-auto p-3.5 sm:p-6 md:p-8 pb-36 md:pb-16 touch-pan-y overscroll-contain select-none scrollbar-thin scrollbar-thumb-zinc-800">
        <div className="flex items-center gap-3 mb-4 sm:mb-6 shrink-0">
          <button
            onClick={() => setSelectedDetailSeries(null)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-800 text-xs font-bold transition cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Voltar para {activeSeriesCategory || 'Favoritos'}</span>
          </button>
        </div>

        <div className="relative rounded-2xl md:rounded-3xl bg-zinc-900 border border-zinc-800 p-4 sm:p-6 md:p-8 flex flex-col md:flex-row gap-5 md:gap-8 shadow-2xl mb-8 min-h-[300px] overflow-hidden shrink-0">
          <div className="absolute inset-0 z-0 opacity-15 overflow-hidden pointer-events-none">
            <img
              src={getSafeImageUrl(selectedDetailSeries.backdrop || selectedDetailSeries.poster)}
              alt=""
              className="w-full h-full object-cover blur-3xl scale-125"
            />
          </div>

          {/* Left Column: ONLY the True Proportion Poster */}
          <div className="w-36 sm:w-48 md:w-52 shrink-0 mx-auto md:mx-0 z-10 flex items-center justify-center">
            <img
              src={getSafeImageUrl(selectedDetailSeries.poster)}
              alt={selectedDetailSeries.title}
              className="w-full h-auto max-h-[300px] md:max-h-[340px] object-contain rounded-2xl shadow-2xl border border-zinc-800/80 bg-black/60"
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=500';
              }}
            />
          </div>

          {/* Right Column: Series Details, Synopsis, & Action Buttons */}
          <div className="flex-1 flex flex-col justify-start gap-4 relative z-10 min-w-0">
            <div>
              <div className="flex flex-wrap items-center gap-2.5 mb-3">
                {getDisplayYear(selectedDetailSeries.title, selectedDetailSeries.year) && (
                  <span className="px-3 py-1 rounded-xl bg-theme-accent text-black font-black text-xs">
                    {getDisplayYear(selectedDetailSeries.title, selectedDetailSeries.year)}
                  </span>
                )}
                <span className="px-3 py-1 rounded-xl bg-zinc-800 text-zinc-300 font-bold text-xs flex items-center gap-1.5">
                  <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                  {(selectedDetailSeries.score ? selectedDetailSeries.score : 8.5).toFixed(1)}
                </span>
                <span className="px-3 py-1 rounded-xl bg-zinc-800 text-zinc-300 font-bold text-xs">
                  {selectedDetailSeries.category}
                </span>
                <span className="px-3 py-1 rounded-xl bg-zinc-800 text-zinc-300 font-bold text-xs">
                  {selectedDetailSeries.episodes.length} episódios
                </span>
              </div>

              <h1 className="text-2xl md:text-4xl font-black text-white mb-3">
                {selectedDetailSeries.title}
              </h1>

              <div className="mb-4">
                <RatingsBadges score={selectedDetailSeries.score || 8.5} size="md" />
              </div>

              <p className="text-sm md:text-base text-zinc-300 leading-relaxed">
                {isFetchingTmdb ? (
                  <span className="inline-flex items-center gap-2 text-zinc-400">
                    <Loader2 className="w-4 h-4 animate-spin text-theme-accent" />
                    Carregando sinopse completa...
                  </span>
                ) : (
                  tmdbDetails?.synopsis ||
                  selectedDetailSeries.synopsis ||
                  selectedDetailSeries.description ||
                  'Série completa em alta definição com todas as temporadas e episódios disponíveis.'
                )}
              </p>
            </div>

            {/* Action Buttons Section - Responsivo para Tablet Vertical e Smartphone */}
            <div className="pt-3 border-t border-zinc-800/80 mt-auto w-full">
              <div className="flex flex-wrap lg:flex-nowrap items-center gap-2 w-full">
                {/* 1. Botão Principal: Continuar / Assistir Episódio */}
                {hasSeriesProg && seriesProg.lastEpisode ? (
                  <>
                    <button
                      id="series-main-play-btn"
                      onClick={() => playEpisode(selectedDetailSeries, seriesProg.lastEpisode!, false)}
                      className="relative overflow-hidden flex-1 min-w-[150px] px-3.5 py-2.5 rounded-xl bg-theme-accent hover:opacity-95 text-black font-black text-xs sm:text-sm flex items-center justify-center gap-1.5 shadow-lg shadow-theme-accent-sm transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-white"
                    >
                      <Play className="w-4 h-4 fill-current shrink-0" />
                      <span className="truncate">
                        Continuar T{seriesProg.seasonNumber}:E{seriesProg.episodeNumber}
                      </span>
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/25">
                        <div
                          className="h-full bg-black/75 transition-all duration-300"
                          style={{ width: `${seriesProg.episodeProgress.percentage}%` }}
                        />
                      </div>
                    </button>

                    <button
                      onClick={() => playEpisode(selectedDetailSeries, seriesProg.lastEpisode!, true)}
                      className="px-3 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-xs sm:text-sm flex items-center justify-center gap-1.5 border border-zinc-700 shrink-0 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-white"
                      title="Assistir o episódio atual do minuto 0:00"
                    >
                      <RotateCcw className="w-3.5 h-3.5 text-theme-accent" />
                      <span className="whitespace-nowrap hidden lg:inline">Do Início</span>
                    </button>

                    <button
                      onClick={() => {
                        clearContentProgress(
                          selectedDetailSeries.id,
                          selectedDetailSeries.title,
                          selectedDetailSeries.episodes?.map((ep) => ep.id)
                        );
                      }}
                      className="p-2.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-red-400 border border-zinc-800 shrink-0 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-white"
                      title="Limpar histórico de exibição desta série"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  hasLoadedRealEpisodes && episodesInSeason.length > 0 ? (
                    <button
                      id="series-main-play-btn"
                      onClick={() => playEpisode(selectedDetailSeries, episodesInSeason[0], true)}
                      className="flex-1 min-w-[150px] px-4 py-2.5 rounded-xl bg-theme-accent text-black font-black text-xs sm:text-sm flex items-center justify-center gap-2 shadow-lg shadow-theme-accent-sm transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-white"
                    >
                      <Play className="w-4 h-4 fill-current shrink-0" />
                      <span className="truncate">Assistir Episódio 1</span>
                    </button>
                  ) : (
                    <button
                      disabled
                      className="flex-1 min-w-[150px] px-4 py-2.5 rounded-xl bg-zinc-800 text-zinc-400 font-bold text-xs sm:text-sm flex items-center justify-center gap-2 border border-zinc-700 cursor-not-allowed"
                    >
                      <Loader2 className="w-4 h-4 animate-spin text-theme-accent" />
                      <span>Carregando Episódios...</span>
                    </button>
                  )
                )}

                {/* 2. Botão: Temporada Assistida (Marca todos os episódios da temporada atual) */}
                {(() => {
                  const isCurrentSeasonAllWatched = episodesInSeason.length > 0 && episodesInSeason.every((ep) => isContentWatched(ep.id));
                  return (
                    <button
                      onClick={() => {
                        const epIds = episodesInSeason.map((ep) => ep.id);
                        toggleWatched(epIds, false, undefined, !isCurrentSeasonAllWatched);
                      }}
                      className={`flex-1 min-w-[130px] px-3 py-2.5 rounded-xl border text-xs sm:text-sm font-bold flex items-center justify-center gap-1.5 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-white ${
                        isCurrentSeasonAllWatched
                          ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/30'
                          : 'bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border-zinc-800 hover:border-emerald-500/40'
                      }`}
                      title={isCurrentSeasonAllWatched ? `Desmarcar temporada ${selectedSeason}` : `Marcar temporada ${selectedSeason} como assistida`}
                    >
                      <CheckCircle2 className={`w-4 h-4 shrink-0 ${isCurrentSeasonAllWatched ? 'text-emerald-400' : 'text-zinc-400'}`} />
                      <span className="truncate">
                        {isCurrentSeasonAllWatched ? `T${selectedSeason} Assistida ✓` : `T${selectedSeason} Assistida`}
                      </span>
                    </button>
                  );
                })()}

                {/* 3. Botão: Marcar Série Inteira como Assistida */}
                <button
                  onClick={() => toggleWatched(selectedDetailSeries.id, true, selectedDetailSeries.episodes)}
                  className={`flex-1 min-w-[130px] px-3 py-2.5 rounded-xl border text-xs sm:text-sm font-bold flex items-center justify-center gap-1.5 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-white ${
                    isContentWatched(selectedDetailSeries.id)
                      ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/30'
                      : 'bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border-zinc-800 hover:border-emerald-500/40'
                  }`}
                  title={isContentWatched(selectedDetailSeries.id) ? 'Desmarcar série inteira' : 'Marcar série inteira como assistida'}
                >
                  <CheckCircle2 className={`w-4 h-4 shrink-0 ${isContentWatched(selectedDetailSeries.id) ? 'text-emerald-400' : 'text-zinc-400'}`} />
                  <span className="truncate">{isContentWatched(selectedDetailSeries.id) ? 'Série Assistida ✓' : 'Série Assistida'}</span>
                </button>

                {/* 4. Botão: Favoritos */}
                <button
                  onClick={() => toggleFavorite(selectedDetailSeries.id)}
                  className={`flex-1 min-w-[110px] px-3 py-2.5 rounded-xl border text-xs sm:text-sm font-bold flex items-center justify-center gap-1.5 transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-white ${
                    isFavorite(selectedDetailSeries.id)
                      ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                      : 'bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border-zinc-800'
                  }`}
                >
                  <Star
                    className={`w-4 h-4 shrink-0 ${
                      isFavorite(selectedDetailSeries.id) ? 'fill-current text-amber-400' : 'text-zinc-400'
                    }`}
                  />
                  <span className="truncate">
                    {isFavorite(selectedDetailSeries.id) ? 'Favorito' : 'Favoritar'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Seasons Tabs - Navegador Completo com Setas, Scroll e D-Pad */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3 gap-3">
            <div className="flex items-center gap-2.5">
              <h3 className="text-sm font-bold text-zinc-400">Temporadas ({seasons.length})</h3>
              {seasons.length > 3 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={scrollSeasonsLeft}
                    className="p-1.5 rounded-lg bg-zinc-800/90 hover:bg-zinc-700 text-zinc-300 hover:text-white border border-zinc-700/80 transition cursor-pointer active:scale-90"
                    title="Temporadas anteriores"
                    aria-label="Temporadas anteriores"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={scrollSeasonsRight}
                    className="p-1.5 rounded-lg bg-zinc-800/90 hover:bg-zinc-700 text-zinc-300 hover:text-white border border-zinc-700/80 transition cursor-pointer active:scale-90"
                    title="Próximas temporadas"
                    aria-label="Próximas temporadas"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
            {isLoadingSeriesDetails && (
              <span className="text-xs text-theme-accent flex items-center gap-1.5 font-bold">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Carregando temporadas e episódios...
              </span>
            )}
          </div>

          <div className="relative group/seasons">
            {/* Seta Flutuante Esquerda */}
            {seasons.length > 4 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  scrollSeasonsLeft();
                }}
                className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 w-8 h-8 items-center justify-center rounded-full bg-black/85 hover:bg-zinc-800 text-white/90 hover:text-white border border-zinc-700 shadow-xl transition active:scale-90 cursor-pointer"
                title="Temporadas anteriores"
                aria-label="Temporadas anteriores"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}

            <div
              ref={seasonsScrollRef}
              onWheel={(e) => {
                if (seasonsScrollRef.current && e.deltaY !== 0) {
                  e.preventDefault();
                  seasonsScrollRef.current.scrollLeft += e.deltaY;
                }
              }}
              className="flex items-center gap-2 overflow-x-auto pb-2 scroll-smooth px-1 sm:px-10 touch-pan-x select-none scrollbar-thin scrollbar-thumb-zinc-800 hover:scrollbar-thumb-zinc-700"
            >
              {seasons.map((seasonNum, sIdx) => {
                const seasonEpisodes = episodesList.filter((e) => Number(e.seasonNumber || 1) === seasonNum);
                const isSeasonAllWatched = seasonEpisodes.length > 0 && seasonEpisodes.every((e) => isContentWatched(e.id));
                const isSelected = selectedSeason === seasonNum;

                return (
                  <button
                    key={seasonNum}
                    id={`season-btn-${seasonNum}`}
                    onClick={() => {
                      setSelectedSeason(seasonNum);
                      document.getElementById(`season-btn-${seasonNum}`)?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowRight') {
                        if (sIdx < seasons.length - 1) {
                          e.preventDefault();
                          const nextNum = seasons[sIdx + 1];
                          setSelectedSeason(nextNum);
                          const nextEl = document.getElementById(`season-btn-${nextNum}`);
                          nextEl?.focus();
                          nextEl?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                        } else {
                          // No final da linha de temporadas, desce para o primeiro episódio
                          e.preventDefault();
                          const firstEp = document.getElementById('first-episode-card');
                          firstEp?.focus();
                          firstEp?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                        }
                      } else if (e.key === 'ArrowLeft') {
                        if (sIdx > 0) {
                          e.preventDefault();
                          const prevNum = seasons[sIdx - 1];
                          setSelectedSeason(prevNum);
                          const prevEl = document.getElementById(`season-btn-${prevNum}`);
                          prevEl?.focus();
                          prevEl?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                        }
                      } else if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        const firstEp = document.getElementById('first-episode-card');
                        firstEp?.focus();
                        firstEp?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        const mainPlayBtn = document.getElementById('series-main-play-btn');
                        mainPlayBtn?.focus();
                        mainPlayBtn?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                      }
                    }}
                    className={`px-4 py-2.5 rounded-2xl text-xs font-bold transition cursor-pointer border whitespace-nowrap flex items-center gap-1.5 shrink-0 focus:outline-none focus:ring-2 focus:ring-white ${
                      isSelected
                        ? 'bg-theme-accent text-black border-theme-accent font-black shadow-md shadow-theme-accent-sm scale-105'
                        : isSeasonAllWatched
                        ? 'bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border-emerald-500/40 shadow-sm'
                        : 'bg-zinc-900/80 hover:bg-zinc-800 text-zinc-300 border-zinc-800'
                    }`}
                    title={isSeasonAllWatched ? `Temporada ${seasonNum} - Todos os episódios assistidos` : `Temporada ${seasonNum}`}
                  >
                    {isSeasonAllWatched && (
                      <CheckCircle2 className={`w-3.5 h-3.5 shrink-0 ${isSelected ? 'text-black fill-current' : 'text-emerald-400'}`} />
                    )}
                    <span>Temporada {seasonNum}</span>
                  </button>
                );
              })}
            </div>

            {/* Seta Flutuante Direita */}
            {seasons.length > 4 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  scrollSeasonsRight();
                }}
                className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 w-8 h-8 items-center justify-center rounded-full bg-black/85 hover:bg-zinc-800 text-white/90 hover:text-white border border-zinc-700 shadow-xl transition active:scale-90 cursor-pointer"
                title="Próximas temporadas"
                aria-label="Próximas temporadas"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Episodes List */}
        <div className="space-y-2">
          <h3 className="text-sm font-bold text-zinc-400 mb-3">
            Episódios (Temporada {selectedSeason}) - {hasLoadedRealEpisodes ? episodesInSeason.length : 0} episódios
          </h3>
          {isLoadingSeriesDetails ? (
            <div className="p-8 rounded-2xl bg-zinc-900/40 border border-zinc-800 text-center flex flex-col items-center justify-center gap-2">
              <OpenTvLoader size="compact" glow="none" className="mb-2" />
              <p className="text-sm text-zinc-300 font-bold">Buscando episódios desta temporada...</p>
              <p className="text-xs text-zinc-500">Conectando ao servidor para carregar os capítulos.</p>
            </div>
          ) : !hasLoadedRealEpisodes ? (
            <div className="p-8 rounded-2xl bg-zinc-900/40 border border-zinc-800 text-center flex flex-col items-center justify-center gap-3">
              <p className="text-sm text-amber-300 font-bold">{seriesDetailsError || 'Episódios não encontrados no servidor.'}</p>
              <button
                type="button"
                onClick={() => selectedDetailSeries && handleSelectSeries(selectedDetailSeries)}
                className="px-4 py-2 rounded-xl bg-theme-accent hover:opacity-90 text-white font-bold text-xs transition cursor-pointer flex items-center gap-2 mx-auto shadow-md"
              >
                <RotateCcw className="w-4 h-4" /> Tentar Novamente
              </button>
            </div>
          ) : episodesInSeason.length === 0 ? (
            <div className="p-8 rounded-2xl bg-zinc-900/40 border border-zinc-800 text-center">
              <p className="text-sm text-zinc-400">Nenhum episódio cadastrado para a Temporada {selectedSeason}.</p>
            </div>
          ) : (
            episodesInSeason.map((ep, epIdx) => {
              const isEpWatched = isContentWatched(ep.id);
              const epProg = getContentProgress(ep.id);
              const epHasWatched = epProg && epProg.watchedSeconds > 10 && !epProg.isCompleted;

              return (
                <div
                  key={ep.id}
                  id={epIdx === 0 ? 'first-episode-card' : `episode-card-${ep.id}`}
                  tabIndex={0}
                  role="button"
                  onClick={() => playEpisode(selectedDetailSeries, ep, !epHasWatched)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      playEpisode(selectedDetailSeries, ep, !epHasWatched);
                    } else if (e.key === 'ArrowUp' && epIdx === 0) {
                      e.preventDefault();
                      const seasonBtn = document.getElementById(`season-btn-${selectedSeason}`);
                      seasonBtn?.focus();
                      seasonBtn?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                    }
                  }}
                  className={`p-3.5 sm:p-4 rounded-2xl bg-zinc-900/70 hover:bg-zinc-850 border border-zinc-800/80 transition flex items-center justify-between gap-3 sm:gap-4 cursor-pointer group focus:outline-none focus:ring-2 focus:ring-theme-accent focus:border-theme-accent focus:bg-zinc-850 shadow-sm ${
                    isEpWatched ? 'opacity-50 hover:opacity-100 bg-zinc-950/40 border-zinc-850' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    {ep.thumbnail ? (
                      <div className="w-16 h-12 rounded-xl bg-black/60 border border-zinc-800 overflow-hidden shrink-0 relative">
                        <img
                          src={getSafeImageUrl(ep.thumbnail)}
                          alt={ep.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition">
                          <Play className="w-4 h-4 fill-white text-white" />
                        </div>
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-2xl bg-theme-accent-subtle text-theme-accent border border-theme-accent-border flex items-center justify-center font-black shrink-0 group-hover:bg-theme-accent group-hover:text-black group-focus:bg-theme-accent group-focus:text-black transition relative overflow-hidden">
                        <Play className="w-5 h-5 fill-current ml-0.5" />
                        {epHasWatched && (
                          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black">
                            <div
                              className="h-full bg-theme-accent"
                              style={{ width: `${epProg.percentage}%` }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    <div className="min-w-0">
                      <h4 className="text-sm font-bold text-white group-hover:text-amber-300 group-focus:text-amber-300 transition truncate flex items-center gap-2">
                        <span>{ep.episodeNumber}. {ep.title}</span>
                        {isEpWatched && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Assistido
                          </span>
                        )}
                        {epHasWatched && !isEpWatched && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-theme-accent-subtle text-theme-accent-light border border-theme-accent-border">
                            {epProg.formattedProgress} ({epProg.percentage}%)
                          </span>
                        )}
                      </h4>
                      <p className="text-xs text-zinc-400 truncate mt-0.5">
                        {ep.duration ? `${ep.duration} min` : '45 min'} • {epHasWatched ? `Restam ${epProg.formattedRemaining}` : (ep.synopsis || 'Assistir episódio')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      tabIndex={-1}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleWatched(ep.id);
                      }}
                      className={`p-2 rounded-xl border transition ${
                        isEpWatched
                          ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/30'
                          : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white border-zinc-700'
                      }`}
                      title={isEpWatched ? 'Marcar como não assistido' : 'Marcar como assistido'}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </button>

                    {epHasWatched ? (
                      <div className="px-3.5 py-1.5 rounded-xl bg-theme-accent text-black font-black text-xs flex items-center gap-1.5 shadow">
                        <Play className="w-3.5 h-3.5 fill-current" />
                        <span>Continuar ({epProg.formattedProgress})</span>
                      </div>
                    ) : (
                      <div className="px-3.5 py-1.5 rounded-xl bg-theme-accent text-black font-black text-xs flex items-center gap-1.5 shadow">
                        <Play className="w-3.5 h-3.5 fill-current" />
                        <span>Reproduzir</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex bg-zinc-950 overflow-hidden select-none">
      {/* LEFT SIDEBAR: Desktop / Tablet */}
      <div className="hidden md:flex md:w-72 lg:w-80 shrink-0 bg-zinc-950 border-r border-zinc-800/80 flex-col h-full overflow-hidden">
        {/* Sidebar Header & Search */}
        <div className="p-4 border-b border-zinc-800/80 shrink-0 bg-zinc-900/40">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs md:text-sm font-black text-white flex items-center gap-2">
              <Layers className="w-4 h-4 text-theme-accent" />
              <span>Categorias de Séries ({seriesCategories.length})</span>
            </h2>
          </div>

          <div className="relative">
            <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder="Filtrar categorias..."
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full pl-9 pr-8 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-theme-accent"
            />
            {categoryFilter && (
              <button
                type="button"
                onClick={() => setCategoryFilter('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-5 h-5 rounded-full flex items-center justify-center text-zinc-400 hover:text-white bg-zinc-800/90 hover:bg-zinc-700 transition cursor-pointer shadow-sm"
                title="Limpar filtro de categorias"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Categories Vertical List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-thin scrollbar-thumb-zinc-800">
          {filteredCategoryCards.map((category) => {
            const isSelected = activeSeriesCategory === category;
            const isFav = category === 'Favoritos';
            const isContinue = category === 'Continuar Assistindo';
            const isSpecial = isFav || isContinue;
            const isCategoryFav = !isSpecial && isCategoryFavorite('series', category);

            const count = isContinue
              ? continueWatchingSeriesCount
              : isFav
              ? series.filter((s) => isFavorite(s.id)).length
              : series.filter((s) => {
                  const rename = categoryConfig.series.renames[s.category];
                  return s.category === category || rename === category;
                }).length;

            return (
              <div
                key={category}
                className={`w-full group/cat relative flex items-center rounded-xl transition cursor-pointer ${
                  isSelected
                    ? 'bg-theme-accent text-black font-black shadow-md shadow-theme-accent-sm'
                    : isCategoryFav
                    ? 'bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/30'
                    : 'bg-zinc-900/40 hover:bg-zinc-900 text-zinc-300 hover:text-white border border-transparent hover:border-zinc-800'
                }`}
              >
                <button
                  onClick={() => {
                    setActiveSeriesCategory(category);
                    setSeriesSearch('');
                    setCategoryFilter('');
                  }}
                  className="flex-1 text-left px-3 py-2.5 text-xs font-bold flex items-center justify-between gap-2 min-w-0 cursor-pointer"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    {isFav ? (
                      <Star
                        className={`w-3.5 h-3.5 shrink-0 ${
                          isSelected ? 'fill-black' : 'text-amber-400 fill-amber-400'
                        }`}
                      />
                    ) : isContinue ? (
                      <History
                        className={`w-3.5 h-3.5 shrink-0 ${
                          isSelected ? 'text-black' : 'text-amber-400'
                        }`}
                      />
                    ) : isCategoryFav ? (
                      <Star
                        className={`w-3.5 h-3.5 shrink-0 fill-amber-400 ${
                          isSelected ? 'text-black fill-black' : 'text-amber-400'
                        }`}
                      />
                    ) : (
                      <Tv2
                        className={`w-3.5 h-3.5 shrink-0 ${
                          isSelected ? 'text-black' : 'text-zinc-500 group-hover/cat:text-theme-accent'
                        }`}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <AutoScrollTitle
                        text={category}
                        className={isSelected ? 'text-black font-black' : ''}
                      />
                    </div>
                  </div>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full font-mono shrink-0 ${
                      isSelected
                        ? 'bg-black/20 text-black font-black'
                        : isCategoryFav
                        ? 'bg-amber-500/20 text-amber-300 font-bold'
                        : 'bg-zinc-800 text-zinc-400'
                    }`}
                  >
                    {count}
                  </span>
                </button>

                {/* Star / Favorite Toggle Button for Category */}
                {!isSpecial && (
                  <button
                    type="button"
                    title={isCategoryFav ? 'Remover categoria dos favoritos' : 'Favoritar categoria'}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCategoryFavorite('series', category);
                    }}
                    className={`pr-2.5 pl-1 py-2 transition cursor-pointer ${
                      isSelected
                        ? 'text-black hover:opacity-75'
                        : isCategoryFav
                        ? 'text-amber-400 opacity-100 hover:scale-125'
                        : 'text-zinc-500 opacity-0 group-hover/cat:opacity-100 hover:text-amber-400 hover:scale-125'
                    }`}
                  >
                    <Star
                      className={`w-3.5 h-3.5 ${
                        isCategoryFav
                          ? isSelected
                            ? 'fill-black'
                            : 'fill-amber-400'
                          : ''
                      }`}
                    />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* CENTER/RIGHT PANEL: Series in Card or List view */}
      <div className="flex-1 flex flex-col bg-zinc-950 h-full overflow-hidden">
        {/* Mobile Header with 3 pausinhos (Menu) to open vertical categories */}
        <div className="md:hidden p-3 border-b border-zinc-800/80 bg-zinc-900/70 flex items-center justify-between gap-2.5 shrink-0">
          <button
            type="button"
            onClick={() => setIsMobileCategoryDrawerOpen(true)}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl font-bold text-xs cursor-pointer shrink-0 active:scale-95 transition bg-theme-accent-subtle border border-theme-accent-border text-theme-accent-light shadow-theme-accent-sm ring-1 ring-theme-accent hover:opacity-90 ${
              displayedSeries.length === 0 ? 'animate-pulse' : ''
            }`}
            title="Escolha a categoria"
          >
            <Menu className="w-4 h-4 text-theme-accent" />
            <span>Escolha a categoria</span>
          </button>

          <div className="relative flex-1 min-w-0">
            <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder="Pesquisar série..."
              value={seriesSearch}
              onChange={(e) => {
                setSeriesSearch(e.target.value);
                setIsMobileCategoryDrawerOpen(false);
              }}
              className="w-full pl-9 pr-8 py-2 bg-zinc-950 border border-zinc-800 rounded-xl text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-theme-accent"
            />
            {seriesSearch && (
              <button
                type="button"
                onClick={() => setSeriesSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-5 h-5 rounded-full flex items-center justify-center text-zinc-400 hover:text-white bg-zinc-800/90 hover:bg-zinc-700 transition cursor-pointer shadow-sm"
                title="Limpar busca de séries"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Mobile Vertical Drawer (Menu vertical um abaixo do outro) */}
        {isMobileCategoryDrawerOpen && (
          <div className="fixed inset-0 z-50 flex bg-black/80 backdrop-blur-sm md:hidden animate-in fade-in duration-200">
            <div className="w-80 max-w-[85vw] bg-zinc-950 border-r border-zinc-800 h-full flex flex-col shadow-2xl animate-in slide-in-from-left duration-200">
              {/* Drawer Header */}
              <div className="p-4 border-b border-zinc-800 flex items-center justify-between bg-zinc-900/60">
                <h3 className="text-sm font-black text-white flex items-center gap-2">
                  <Layers className="w-4 h-4 text-theme-accent" />
                  <span>Categorias ({seriesCategories.length})</span>
                </h3>
                <button
                  onClick={() => setIsMobileCategoryDrawerOpen(false)}
                  className="p-1.5 rounded-xl text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Drawer Search (Filtro exclusivo de categorias) */}
              <div className="p-3 border-b border-zinc-800 bg-zinc-900/40">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Filtrar categorias..."
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full pl-9 pr-8 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-theme-accent"
                  />
                  {categoryFilter && (
                    <button
                      type="button"
                      onClick={() => setCategoryFilter('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-5 h-5 rounded-full flex items-center justify-center text-zinc-400 hover:text-white bg-zinc-800/90 hover:bg-zinc-700 transition cursor-pointer shadow-sm"
                      title="Limpar filtro"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* Vertical List one below the other */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-thin scrollbar-thumb-zinc-800">
                {filteredCategoryCards.map((category) => {
                  const isSelected = activeSeriesCategory === category;
                  const isFav = category === 'Favoritos';
                  const isContinue = category === 'Continuar Assistindo';
                  const isSpecial = isFav || isContinue;
                  const isCategoryFav = !isSpecial && isCategoryFavorite('series', category);

                  const count = isContinue
                    ? continueWatchingSeriesCount
                    : isFav
                    ? series.filter((s) => isFavorite(s.id)).length
                    : series.filter((s) => {
                        const rename = categoryConfig.series.renames[s.category];
                        return s.category === category || rename === category;
                      }).length;

                  return (
                    <div
                      key={category}
                      className={`w-full flex items-center rounded-xl transition cursor-pointer ${
                        isSelected
                          ? 'bg-theme-accent text-black font-black shadow-md shadow-theme-accent-sm'
                          : isCategoryFav
                          ? 'bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/30'
                          : 'bg-zinc-900/40 hover:bg-zinc-900 text-zinc-300 hover:text-white border border-transparent hover:border-zinc-800'
                      }`}
                    >
                      <button
                        onClick={() => {
                          setActiveSeriesCategory(category);
                          setSeriesSearch('');
                          setCategoryFilter('');
                          setIsMobileCategoryDrawerOpen(false);
                        }}
                        className="flex-1 text-left px-3.5 py-3 text-xs font-bold flex items-center justify-between gap-2 min-w-0 cursor-pointer"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          {isFav ? (
                            <Star className={`w-4 h-4 shrink-0 ${isSelected ? 'fill-black' : 'text-amber-400 fill-amber-400'}`} />
                          ) : isContinue ? (
                            <History className={`w-4 h-4 shrink-0 ${isSelected ? 'text-black' : 'text-amber-400'}`} />
                          ) : isCategoryFav ? (
                            <Star className={`w-4 h-4 shrink-0 fill-amber-400 ${isSelected ? 'text-black fill-black' : 'text-amber-400'}`} />
                          ) : (
                            <Tv2 className={`w-4 h-4 shrink-0 ${isSelected ? 'text-black' : 'text-zinc-500'}`} />
                          )}
                          <span className="truncate">{category}</span>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono shrink-0 ${isSelected ? 'bg-black/20 text-black font-black' : 'bg-zinc-800 text-zinc-400'}`}>
                          {count}
                        </span>
                      </button>

                      {/* Botão da Estrela no Drawer Mobile */}
                      {!isSpecial && (
                        <button
                          type="button"
                          title={isCategoryFav ? 'Remover categoria dos favoritos' : 'Favoritar categoria'}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCategoryFavorite('series', category);
                          }}
                          className={`pr-3 pl-1 py-3 transition cursor-pointer shrink-0 ${
                            isSelected
                              ? 'text-black hover:opacity-75'
                              : isCategoryFav
                              ? 'text-amber-400 opacity-100 hover:scale-125'
                              : 'text-zinc-500 opacity-60 hover:opacity-100 hover:text-amber-400 hover:scale-125'
                          }`}
                        >
                          <Star
                            className={`w-4 h-4 ${
                              isCategoryFav
                                ? isSelected
                                  ? 'fill-black'
                                  : 'fill-amber-400'
                                : ''
                            }`}
                          />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex-1 cursor-pointer" onClick={() => setIsMobileCategoryDrawerOpen(false)} />
          </div>
        )}

        {/* Top Control Bar */}
        <div className="p-3 sm:p-4 md:p-6 border-b border-zinc-800/80 bg-zinc-900/40 flex items-center justify-between gap-2.5 shrink-0">
          <div className="min-w-0 flex-1 flex items-center gap-2">
            <h1 className="text-sm sm:text-base md:text-xl font-black text-white truncate">
              {seriesSearch.trim()
                ? `Busca: "${seriesSearch.trim()}" (${categorySeries.length} ${categorySeries.length === 1 ? 'série' : 'séries'})`
                : (activeSeriesCategory || 'Favoritos')}
            </h1>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {/* Campo de Busca de Séries no Desktop (Top Bar) */}
            <div className="hidden md:flex relative w-44 lg:w-60">
              <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                placeholder="Pesquisar série..."
                value={seriesSearch}
                onChange={(e) => setSeriesSearch(e.target.value)}
                className="w-full pl-9 pr-8 py-1.5 bg-zinc-900 border border-zinc-800 rounded-xl text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-theme-accent transition"
              />
              {seriesSearch && (
                <button
                  type="button"
                  onClick={() => setSeriesSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-5 h-5 rounded-full flex items-center justify-center text-zinc-400 hover:text-white bg-zinc-800/90 hover:bg-zinc-700 transition cursor-pointer shadow-sm"
                  title="Limpar busca de séries"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            {/* Clear all Continue Watching for Series */}
            {activeSeriesCategory === 'Continuar Assistindo' && displayedSeries.length > 0 && (
              <button
                onClick={() => {
                  if (window.confirm('Deseja limpar todo o histórico de Continuar Assistindo de séries deste perfil?')) {
                    clearAllWatchHistory();
                  }
                }}
                className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-zinc-900 hover:bg-red-950 border border-zinc-800 hover:border-red-500/40 text-xs font-bold text-zinc-400 hover:text-red-400 transition cursor-pointer"
                title="Limpar todas as séries de Continuar Assistindo"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Limpar Histórico</span>
              </button>
            )}

            {/* Toggle Favorite on Active Category */}
            {activeSeriesCategory && activeSeriesCategory !== 'Favoritos' && activeSeriesCategory !== 'Continuar Assistindo' && (
              <button
                onClick={() => toggleCategoryFavorite('series', activeSeriesCategory)}
                className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl border text-xs font-bold transition cursor-pointer ${
                  isCategoryFavorite('series', activeSeriesCategory)
                    ? 'bg-amber-500/15 border-amber-500/40 text-amber-400 shadow-sm'
                    : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-700'
                }`}
                title={isCategoryFavorite('series', activeSeriesCategory) ? 'Remover dos favoritos' : 'Favoritar Categoria'}
              >
                <Star
                  className={`w-3.5 h-3.5 shrink-0 ${
                    isCategoryFavorite('series', activeSeriesCategory)
                      ? 'fill-amber-400 text-amber-400'
                      : ''
                  }`}
                />
                <span className="hidden sm:inline">
                  {isCategoryFavorite('series', activeSeriesCategory)
                    ? 'Favorita'
                    : 'Favoritar Categoria'}
                </span>
              </button>
            )}

            {/* View Mode Toggle (Cards vs List) */}
            <div className="flex items-center bg-zinc-900 p-0.5 rounded-xl border border-zinc-800 shrink-0">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-1.5 sm:p-2 rounded-lg text-xs font-bold transition cursor-pointer ${
                  viewMode === 'cards'
                    ? 'bg-theme-accent text-black shadow-sm'
                    : 'text-zinc-400 hover:text-white'
                }`}
                title="Visualização em Cards"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 sm:p-2 rounded-lg text-xs font-bold transition cursor-pointer ${
                  viewMode === 'list'
                    ? 'bg-theme-accent text-black shadow-sm'
                    : 'text-zinc-400 hover:text-white'
                }`}
                title="Visualização em Lista"
              >
                <ListIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Series Content Area */}
        <div
          ref={contentAreaRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-4 md:p-6 scrollbar-thin scrollbar-thumb-zinc-800"
        >
          {displayedSeries.length === 0 ? (
            series.length === 0 ? (
              <div className="py-20 text-center max-w-md mx-auto px-4">
                <Tv2 className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">
                  Nenhuma Série Cadastrada
                </h3>
                <p className="text-xs text-zinc-400 mb-4 leading-relaxed">
                  Conecte sua lista IPTV nas configurações para carregar o catálogo de séries com temporadas e episódios.
                </p>
              </div>
            ) : (
              <EmptyCategoryGuide
                type="series"
                categoryName={activeSeriesCategory || 'Continuar Assistindo'}
                availableCategories={filteredCategoryCards}
                onSelectCategory={(cat) => {
                  setActiveSeriesCategory(cat);
                  setSeriesSearch('');
                  setCategoryFilter('');
                }}
                onOpenMobileDrawer={() => setIsMobileCategoryDrawerOpen(true)}
                categoryCounts={categoryCounts}
                totalItemsCount={series.length}
              />
            )
          ) : viewMode === 'cards' ? (
            /* CARDS GRID VIEW */
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3.5">
              {displayedSeries.map((ser) => {
                const fav = isFavorite(ser.id);
                const isWatchedSeries = isContentWatched(ser.id);
                const sProg = getSeriesProgress(ser);
                const hasSProg = sProg?.lastEpisode && sProg?.episodeProgress && sProg.episodeProgress.watchedSeconds > 10;

                return (
                  <div
                    key={ser.id}
                    data-series-card="true"
                    tabIndex={0}
                    role="button"
                    onClick={() => handleSelectSeries(ser)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSelectSeries(ser);
                      } else if (e.key === ' ') {
                        e.preventDefault();
                        toggleFavorite(ser.id);
                      }
                    }}
                    className={`group relative p-3 rounded-2xl bg-zinc-900/70 border border-zinc-800/90 hover:border-theme-accent/50 hover:bg-zinc-800/90 transition-all duration-200 cursor-pointer flex flex-col justify-between select-none shadow-lg focus:outline-none focus:ring-2 focus:ring-theme-accent virtual-card-contain ${
                      isWatchedSeries ? 'opacity-45 hover:opacity-100' : ''
                    }`}
                  >
                    <div className="relative w-full aspect-[2/3] rounded-xl bg-black/60 border border-zinc-800 overflow-hidden mb-2.5">
                      <SeriesPoster
                        poster={ser.poster}
                        title={ser.title}
                        category={ser.category}
                        year={ser.year}
                      />
                      {/* Watched Badge */}
                      {isWatchedSeries && (
                        <div className="absolute top-2 left-2 z-10">
                          <span className="px-2 py-0.5 rounded-lg bg-emerald-950/90 backdrop-blur-md border border-emerald-500/40 text-emerald-400 text-[10px] font-black flex items-center gap-1 shadow">
                            <CheckCircle2 className="w-3 h-3" /> Assistida
                          </span>
                        </div>
                      )}
                      <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
                        {activeSeriesCategory === 'Continuar Assistindo' && (
                          <div
                            aria-hidden="true"
                            onClick={(e) => {
                              e.stopPropagation();
                              clearContentProgress(ser.id, ser.title, ser.episodes?.map((ep) => ep.id));
                            }}
                            className="p-1.5 rounded-xl bg-black/70 backdrop-blur-md text-zinc-400 hover:text-red-400 hover:bg-red-950/80 transition shadow cursor-pointer"
                            title="Remover de Continuar Assistindo"
                          >
                            <X className="w-3.5 h-3.5" />
                          </div>
                        )}
                        <div
                          aria-hidden="true"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(ser.id);
                          }}
                          className="p-1.5 rounded-xl bg-black/70 backdrop-blur-md text-zinc-300 hover:text-amber-400 transition shadow cursor-pointer"
                        >
                          <Star
                            className={`w-3.5 h-3.5 ${
                              fav ? 'fill-amber-400 text-amber-400' : ''
                            }`}
                          />
                        </div>
                      </div>
                      <div className="absolute bottom-2 left-2 z-10 flex items-center gap-1">
                        {getDisplayYear(ser.title, ser.year) && (
                          <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-black/80 text-theme-accent-light border border-theme-accent-border">
                            {getDisplayYear(ser.title, ser.year)}
                          </span>
                        )}
                        {ser.score && (
                          <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-amber-500 text-black flex items-center gap-0.5">
                            <Star className="w-2.5 h-2.5 fill-current" />
                            {ser.score.toFixed(1)}
                          </span>
                        )}
                        {hasSProg && (
                          <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-theme-accent text-black">
                            T{sProg.seasonNumber}:E{sProg.episodeNumber}
                          </span>
                        )}
                      </div>
                      {hasSProg && (
                        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-black/80 z-20">
                          <div
                            className="h-full bg-theme-accent"
                            style={{ width: `${sProg.episodeProgress.percentage}%` }}
                          />
                        </div>
                      )}
                    </div>

                    <div className="min-w-0">
                      <AutoScrollTitle
                        text={ser.title}
                        className="text-xs font-bold text-white group-hover:text-theme-accent-light transition"
                      />
                      <p className="text-[10px] text-zinc-400 truncate">
                        {ser.category}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* LIST VIEW (TABLE) */
            <div className="space-y-1.5">
              {displayedSeries.map((ser) => {
                const fav = isFavorite(ser.id);
                const isWatchedSeries = isContentWatched(ser.id);
                const sProg = getSeriesProgress(ser);
                const hasSProg = sProg?.lastEpisode && sProg?.episodeProgress && sProg.episodeProgress.watchedSeconds > 10;

                return (
                  <div
                    key={ser.id}
                    tabIndex={0}
                    role="button"
                    onClick={() => handleSelectSeries(ser)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSelectSeries(ser);
                      } else if (e.key === ' ') {
                        e.preventDefault();
                        toggleFavorite(ser.id);
                      }
                    }}
                    className={`p-3 rounded-2xl bg-zinc-900/60 border border-zinc-800/80 hover:bg-zinc-900 hover:border-zinc-700 transition flex items-center justify-between gap-3 cursor-pointer focus:outline-none focus:ring-2 focus:ring-theme-accent ${
                      isWatchedSeries ? 'opacity-45 hover:opacity-100' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-14 rounded-xl bg-black/60 border border-zinc-800 overflow-hidden shrink-0 relative">
                        <SeriesPoster
                          poster={ser.poster}
                          title={ser.title}
                          category={ser.category}
                          year={ser.year}
                          size="sm"
                        />
                        {hasSProg && (
                          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black z-20">
                            <div
                              className="h-full bg-theme-accent"
                              style={{ width: `${sProg.episodeProgress.percentage}%` }}
                            />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <AutoScrollTitle
                            text={ser.title}
                            className="text-xs md:text-sm font-bold text-white"
                          />
                          {isWatchedSeries && (
                            <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1 shrink-0">
                              <CheckCircle2 className="w-3 h-3" /> Assistida
                            </span>
                          )}
                          {getDisplayYear(ser.title, ser.year) && (
                            <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-theme-accent-subtle text-theme-accent-light border border-theme-accent-border shrink-0">
                              {getDisplayYear(ser.title, ser.year)}
                            </span>
                          )}
                          {ser.score && (
                            <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center gap-0.5 shrink-0">
                              <Star className="w-3 h-3 fill-current" />
                              {ser.score.toFixed(1)}
                            </span>
                          )}
                          {hasSProg && (
                            <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-theme-accent text-black shrink-0">
                              T{sProg.seasonNumber}:E{sProg.episodeNumber}
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-zinc-400 truncate mt-0.5">
                          {ser.category} • {ser.episodes.length} episódios
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleWatched(ser.id, true, ser.episodes);
                        }}
                        className={`p-2 rounded-xl border transition ${
                          isWatchedSeries
                            ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                            : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white border-zinc-700'
                        }`}
                        title={isWatchedSeries ? 'Marcar como não assistida' : 'Marcar como assistida'}
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </button>

                      <div
                        aria-hidden="true"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(ser.id);
                        }}
                        className="p-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-amber-400 transition cursor-pointer"
                      >
                        <Star
                          className={`w-4 h-4 ${
                            fav ? 'fill-amber-400 text-amber-400' : ''
                          }`}
                        />
                      </div>

                      <button
                        onClick={() => handleSelectSeries(ser)}
                        className="px-3.5 py-2 rounded-xl bg-theme-accent hover:opacity-90 text-black font-black text-xs flex items-center gap-1.5 transition"
                      >
                        <span>Ver Série</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {categorySeries.length > visibleLimit && (
            <div className="pt-6 pb-6 flex flex-col items-center justify-center gap-2 opacity-80">
              <OpenTvLoader size="compact" glow="none" />
              <span className="text-[11px] text-zinc-400 font-medium">
                Carregando mais séries... ({displayedSeries.length} de {categorySeries.length})
              </span>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
